import { UserRole } from '@mi-front/core';
import React from 'react';
import { defineMessage } from 'react-intl';
import { Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { AuthorizedRouteContent } from './AuthorizedRouteContent';
import { PostHogPageviewTracker } from './PostHogPageviewTracker';

export const noPermissionMessage = defineMessage({
  id: 'auth.noPermissionForPage',
  defaultMessage: 'No tienes permisos para acceder a esta página',
});

export type AppRouteProps<
  Path extends string = string,
  Params extends { [K: string]: string | undefined } = { [K: string]: string | undefined },
> = RouteProps<Path, Params> & {
  /** When set, only users with one of these roles can access the route. Otherwise redirects to '/'. */
  readonly allowedRole?: UserRole | UserRole[];
};

/**
 * Drop-in wrapper around React Router's Route that tracks pageviews with PostHog
 * using the route's path template and match params. Use this instead of Route
 * wherever you want the current path pattern and params to be sent as super properties.
 * Optionally pass allowedRole to restrict access by role (redirects to '/' when not allowed).
 */
export function AppRoute<
  Path extends string = string,
  Params extends { [K: string]: string | undefined } = { [K: string]: string | undefined },
>({
  component: Component,
  render,
  children,
  path,
  allowedRole,
  ...rest
}: AppRouteProps<Path, Params>) {
  const hasRoleGuard = allowedRole != null;

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        const routeProps = props as unknown as RouteComponentProps<Params>;
        const content =
          Component != null ? (
            <Component {...props} />
          ) : render != null ? (
            render(routeProps)
          ) : typeof children === 'function' ? (
            (children as (p: RouteComponentProps<Params>) => React.ReactNode)(routeProps)
          ) : (
            children
          );
        return (
          <>
            <PostHogPageviewTracker />
            {hasRoleGuard ? (
              <AuthorizedRouteContent allowedRole={allowedRole} content={content} />
            ) : (
              content
            )}
          </>
        );
      }}
    />
  );
}
